import {
  LOGIN_REQUIRED,
  PORTAL_HANDOFF_ATTEMPTS,
  PORTAL_HANDOFF_IN_PROGRESS,
  PORTAL_HANDOFF_PENDING,
  PORTAL_HANDOFF_STARTED_AT,
  PORTAL_RETURN_URL,
  PrivateSubmissionAppRoutes,
} from '@private-crm-app/constants';
import {
  buildPortalSessionCallbackUrl,
  createPortalSessionHandoff,
} from '@private-crm-app/requests/portalSessionHandoff';

/**
 * A portal login round trip (portal -> CRM -> Auth0 -> CRM) must finish inside this
 * window. Auth0 removes `code` from the URL before our code runs, so the pending
 * marker — not the query string — is what keeps the handoff alive.
 */
const HANDOFF_WINDOW_MS = 15 * 60 * 1000;

const MAX_HANDOFF_ATTEMPTS = 3;

const IN_PROGRESS_STALE_MS = 60 * 1000;

const PORTAL_SESSION_HANDOFF_REQUEST_ID = 'citizen-portal/session-handoff';

function logHandoffTransportError(error: unknown): void {
  const transportError = error as { response?: { status?: number } };

  console.error('[Portal Handoff] Failed to create citizen portal session', {
    name: error instanceof Error ? error.name : 'Error',
    status: transportError.response?.status ?? 'unknown',
    requestId: PORTAL_SESSION_HANDOFF_REQUEST_ID,
  });
}

function readHandoffFeatureFlag(): string | undefined {
  // Webpack inlines known REACT_APP_* vars at build time. When this flag is
  // absent from the bundle, `process` may not exist in the browser runtime.
  if (typeof process === 'undefined') {
    return undefined;
  }

  return process.env.REACT_APP_CITIZEN_PORTAL_HANDOFF_ENABLED;
}

export function isCitizenPortalHandoffEnabled(): boolean {
  return readHandoffFeatureFlag() !== 'false';
}

/** Clears portal handoff markers without touching logout_redirect_url. */
export function clearPortalSessionHandoffState(): void {
  sessionStorage.removeItem(PORTAL_RETURN_URL);
  sessionStorage.removeItem(PORTAL_HANDOFF_PENDING);
  sessionStorage.removeItem(PORTAL_HANDOFF_IN_PROGRESS);
  sessionStorage.removeItem(PORTAL_HANDOFF_STARTED_AT);
  sessionStorage.removeItem(PORTAL_HANDOFF_ATTEMPTS);
}

/**
 * Records a portal-initiated login. Must run synchronously on page entry: an
 * already-authenticated citizen resolves auth before any awaited CRM validation.
 */
export function markPortalSessionHandoffPending(portalReturnUrl: string): void {
  const trimmed = portalReturnUrl?.trim();
  if (!trimmed) {
    return;
  }

  sessionStorage.setItem(PORTAL_RETURN_URL, trimmed);
  sessionStorage.setItem(PORTAL_HANDOFF_PENDING, 'true');
  sessionStorage.setItem(PORTAL_HANDOFF_STARTED_AT, Date.now().toString());
  sessionStorage.removeItem(PORTAL_HANDOFF_ATTEMPTS);
  sessionStorage.removeItem(PORTAL_HANDOFF_IN_PROGRESS);
}

function portalReturnUrl(): string | null {
  return sessionStorage.getItem(PORTAL_RETURN_URL)?.trim() || null;
}

/** Older builds cleared `portal_handoff_pending` while leaving the return URL behind. */
function healPortalHandoffMarkers(): boolean {
  const returnUrl = portalReturnUrl();
  if (!returnUrl) {
    return false;
  }

  if (sessionStorage.getItem(PORTAL_HANDOFF_PENDING) !== 'true') {
    sessionStorage.setItem(PORTAL_HANDOFF_PENDING, 'true');
    if (!sessionStorage.getItem(PORTAL_HANDOFF_STARTED_AT)) {
      sessionStorage.setItem(PORTAL_HANDOFF_STARTED_AT, Date.now().toString());
    }
  }

  return true;
}

function clearStaleInProgressFlag(): void {
  if (sessionStorage.getItem(PORTAL_HANDOFF_IN_PROGRESS) !== 'true') {
    return;
  }

  const startedAt = Number(sessionStorage.getItem(PORTAL_HANDOFF_STARTED_AT));
  if (!Number.isFinite(startedAt) || startedAt <= 0) {
    sessionStorage.removeItem(PORTAL_HANDOFF_IN_PROGRESS);

    return;
  }

  if (Date.now() - startedAt > IN_PROGRESS_STALE_MS) {
    sessionStorage.removeItem(PORTAL_HANDOFF_IN_PROGRESS);
  }
}

function isWithinHandoffWindow(): boolean {
  const startedAt = Number(sessionStorage.getItem(PORTAL_HANDOFF_STARTED_AT));
  if (!Number.isFinite(startedAt) || startedAt <= 0) {
    // Markers written before this field existed still get one chance.
    return true;
  }

  return Date.now() - startedAt <= HANDOFF_WINDOW_MS;
}

function attemptCount(): number {
  const raw = Number(sessionStorage.getItem(PORTAL_HANDOFF_ATTEMPTS));

  return Number.isFinite(raw) && raw > 0 ? raw : 0;
}

function logHandoffSkip(reason: string): void {
  console.warn('[Portal Handoff] Skipped:', reason, {
    pending: sessionStorage.getItem(PORTAL_HANDOFF_PENDING),
    returnUrl: portalReturnUrl(),
    attempts: sessionStorage.getItem(PORTAL_HANDOFF_ATTEMPTS),
    inProgress: sessionStorage.getItem(PORTAL_HANDOFF_IN_PROGRESS),
    enabled: isCitizenPortalHandoffEnabled(),
  });
}

/**
 * Portal session handoff is opt-in per navigation and must not intercept
 * existing CRM flows (email verify, login_required issue links, submission create).
 */
export function shouldAttemptPortalSessionHandoff(search: string, pathname: string): boolean {
  if (!isCitizenPortalHandoffEnabled()) {
    logHandoffSkip('feature flag disabled');

    return false;
  }

  if (!healPortalHandoffMarkers()) {
    logHandoffSkip('no portal return URL in sessionStorage');

    return false;
  }

  const params = new URLSearchParams(search);
  if (params.get('verifyToken')) {
    logHandoffSkip('email verification flow');

    return false;
  }

  if (params.get(LOGIN_REQUIRED) === 'true') {
    logHandoffSkip('login_required issue link');

    return false;
  }

  if (pathname === PrivateSubmissionAppRoutes.CREATE) {
    logHandoffSkip('submission create route');

    return false;
  }

  clearStaleInProgressFlag();

  // Expire markers left behind by an abandoned portal login instead of
  // redirecting a citizen who has since navigated the CRM on their own.
  if (!isWithinHandoffWindow()) {
    clearPortalSessionHandoffState();
    logHandoffSkip('handoff window expired');

    return false;
  }

  if (attemptCount() >= MAX_HANDOFF_ATTEMPTS) {
    clearPortalSessionHandoffState();
    logHandoffSkip('max handoff attempts reached');

    return false;
  }

  return true;
}

export async function redirectToCitizenPortalIfNeeded(
  privateAppToken: string | null | undefined,
  search = window.location.search,
  pathname = window.location.pathname
): Promise<boolean> {
  if (!privateAppToken) {
    logHandoffSkip('private app token not ready');

    return false;
  }

  if (!shouldAttemptPortalSessionHandoff(search, pathname)) {
    return false;
  }

  const returnUrl = portalReturnUrl();
  if (!returnUrl) {
    logHandoffSkip('portal return URL missing after guard');

    return false;
  }

  if (sessionStorage.getItem(PORTAL_HANDOFF_IN_PROGRESS) === 'true') {
    logHandoffSkip('handoff already in progress');

    return false;
  }

  sessionStorage.setItem(PORTAL_HANDOFF_IN_PROGRESS, 'true');
  sessionStorage.setItem(PORTAL_HANDOFF_ATTEMPTS, String(attemptCount() + 1));

  try {
    const { code } = await createPortalSessionHandoff(privateAppToken, returnUrl);
    const callbackUrl = buildPortalSessionCallbackUrl(returnUrl, code);
    clearPortalSessionHandoffState();
    window.location.href = callbackUrl;

    return true;
  } catch (error) {
    logHandoffTransportError(error);
    sessionStorage.removeItem(PORTAL_HANDOFF_IN_PROGRESS);
    // Leave PORTAL_HANDOFF_PENDING in place so a retry is possible on the next auth callback.

    return false;
  }
}
